/**
 * The "Credits" tab: the user's subscription credits, with grant and revoke.
 *
 * Lifted from the drawer section of the same name. The list is rendered as stacked rows rather
 * than a DataTable because it is short, unsorted and unpaged — a credit list is a handful of
 * entries, and giving it paging controls would be more furniture than content.
 */

import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { Add, Cancel, CardGiftcard } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import type { CreditStatus, SubscriptionCreditDto } from '@api/types/subscription'
import GrantCreditDialog from '../GrantCreditDialog'
import { adminUserCreditsKey } from './userQueryKeys'
import { useIsAdmin } from './useIsAdmin'
import { creditStatusName, errorMessage, formatDay } from './userFormatting'

export interface UserCreditsPanelProps {
  userId: string
}

const STATUS_COLORS: Record<CreditStatus, 'warning' | 'success' | 'default' | 'error'> = {
  Pending: 'warning',
  Redeemed: 'success',
  Expired: 'default',
  Revoked: 'error',
}

export function UserCreditsPanel({ userId }: UserCreditsPanelProps) {
  const queryClient = useQueryClient()
  // Moderators see the credits read-only; granting and revoking are Admin-only.
  const isAdmin = useIsAdmin()
  const [grantOpen, setGrantOpen] = useState(false)
  // Revoking cannot be undone, so the button asks first.
  const [revokeTarget, setRevokeTarget] = useState<SubscriptionCreditDto | null>(null)

  const {
    data: creditsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: adminUserCreditsKey(userId),
    // The endpoint answers with the same summary object as /api/me/credits, not a bare array —
    // typing it as an array is what made every admin credit list render empty.
    queryFn: () => subscriptionsApi.getUserCredits(userId),
  })

  const credits = creditsResponse?.credits ?? []

  const revokeCredit = useMutation({
    mutationFn: (creditId: string) => subscriptionsApi.revokeCredit(creditId),
    onSuccess: () => {
      setRevokeTarget(null)
      return queryClient.invalidateQueries({ queryKey: adminUserCreditsKey(userId) })
    },
  })

  return (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CardGiftcard fontSize="small" /> Subscription Credits
          </Typography>
          {isAdmin && (
            <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => setGrantOpen(true)}>
              Grant Credit
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(error, 'Failed to load subscription credits')}
          </Alert>
        )}
        {revokeCredit.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(revokeCredit.error, 'Failed to revoke credit')}
          </Alert>
        )}

        {isLoading ? (
          <CircularProgress size={20} />
        ) : error ? null : credits.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No subscription credits
          </Typography>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />} spacing={0}>
            {credits.map((credit) => {
              const status = creditStatusName(credit.status)

              return (
                <Box
                  key={credit.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        {credit.plan.displayName} — {credit.creditMonths}mo
                      </Typography>
                      <Chip label={status} size="small" color={STATUS_COLORS[status]} variant="outlined" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {/* The admin credits endpoint returns the user-shaped DTO, which carries no
                          granter — see subscriptionsApi.getUserCredits. */}
                      {credit.reason} · {formatDay(credit.grantedAt)}
                    </Typography>
                  </Box>

                  {/* Only a credit that has not been used yet can be taken back. */}
                  {isAdmin && status === 'Pending' && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => setRevokeTarget(credit)}
                      disabled={revokeCredit.isPending}
                      sx={{ flexShrink: 0 }}
                    >
                      Revoke
                    </Button>
                  )}
                </Box>
              )
            })}
          </Stack>
        )}
      </Paper>

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke credit?"
        message={
          revokeTarget
            ? `Revoke the unused ${revokeTarget.creditMonths}-month ${revokeTarget.plan.displayName} credit? This cannot be undone.`
            : undefined
        }
        confirmLabel="Revoke"
        destructive
        loading={revokeCredit.isPending}
        onConfirm={() => revokeTarget && revokeCredit.mutate(revokeTarget.id)}
        onClose={() => setRevokeTarget(null)}
      />

      {isAdmin && (
      <GrantCreditDialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        userId={userId}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: adminUserCreditsKey(userId) })}
      />
      )}
    </Box>
  )
}
